import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, {
  INHERENT_LEVEL, tInherentLevel, tLikelihood, tImpact, tRiskConfidence, INHERENT_LEVEL_KEYS,
} from "@/lib/api";
import AppShell from "@/components/layout/AppShell";
import { CritBadge } from "@/components/shared/Badges";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Gauge, ArrowRight, Loader2, UserCheck, ShieldOff, ShieldCheck } from "lucide-react";

function LevelBadge({ level }) {
  const c = INHERENT_LEVEL[level] || INHERENT_LEVEL.LOW;
  return <Badge variant="outline" className={`rounded-sm font-mono text-xs ${c.cls}`} data-testid={`risk-level-${level}`}>{tInherentLevel(level)}</Badge>;
}

export default function Risks() {
  const navigate = useNavigate();
  const [level, setLevel] = useState("all");
  const { data = [], isLoading } = useQuery({
    queryKey: ["risk-overview"],
    queryFn: async () => (await api.get("/risk/services")).data,
  });

  const rows = data.filter((r) => level === "all" || r.inherent.level === level);

  return (
    <AppShell title="Risques" subtitle="Risque INHÉRENT par service — dérivé, explicable, contextualisé (aucun score global)"
      actions={
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-44 rounded-sm" data-testid="risk-filter-level"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les niveaux</SelectItem>
            {INHERENT_LEVEL_KEYS.map((k) => <SelectItem key={k} value={k}>{tInherentLevel(k)}</SelectItem>)}
          </SelectContent>
        </Select>
      }>
      <div className="space-y-6" data-testid="risks-page">
        <div className="border border-border bg-card rounded-sm p-4 flex items-start gap-3">
          <Gauge className="h-5 w-5 text-primary mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Le <span className="text-foreground">risque inhérent</span> est calculé <span className="text-foreground">avant</span> les contrôles et <span className="text-foreground">avant</span> le crédit de résilience (ADR D11c).
            Le contexte de résilience structurelle et l'efficacité créditable des contrôles sont affichés <span className="text-foreground">séparément</span> et <span className="text-yellow-400">non appliqués</span> à ce stade.
            Le risque résiduel (après simulation) est prévu au domaine ultérieur.
          </p>
        </div>

        <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="risks-table">
          {isLoading ? (
            <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calcul des risques…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead>Service</TableHead>
                  <TableHead>Criticité</TableHead>
                  <TableHead>Risque inhérent</TableHead>
                  <TableHead>Vraisemblance × Impact</TableHead>
                  <TableHead>Confiance</TableHead>
                  <TableHead>Contributeurs</TableHead>
                  <TableHead>Décision</TableHead>
                  <TableHead className="text-right">Fiche</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">Aucun service.</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.service.id} className="border-border cursor-pointer" onClick={() => navigate(`/services/${r.service.id}`)} data-testid={`risk-row-${r.service.id}`}>
                    <TableCell className="font-medium">{r.service.name}</TableCell>
                    <TableCell><CritBadge value={r.service.criticality} /></TableCell>
                    <TableCell><LevelBadge level={r.inherent.level} /></TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{tLikelihood(r.inherent.likelihood)} × {tImpact(r.inherent.impact)}</TableCell>
                    <TableCell className="text-xs">{tRiskConfidence(r.inherent.confidence)}</TableCell>
                    <TableCell className="text-xs font-mono">{r.contributor_count}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-xs">
                        {r.has_acceptance ? <UserCheck className="h-3.5 w-3.5 text-green-400" title="Risque accepté" /> : null}
                        {r.has_override ? <ShieldOff className="h-3.5 w-3.5 text-orange-400" title="Override" /> : null}
                        {r.has_decision ? <ShieldCheck className="h-3.5 w-3.5 text-blue-400" title="Décision" /> : null}
                        {!r.has_decision && !r.has_acceptance && !r.has_override && <span className="text-muted-foreground">—</span>}
                        {r.creditable_controls > 0 && <span className="text-[10px] text-muted-foreground">· {r.creditable_controls} ctrl créditable(s)</span>}
                      </span>
                    </TableCell>
                    <TableCell className="text-right"><ArrowRight className="h-4 w-4 text-muted-foreground inline" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AppShell>
  );
}
